/**
 * Two-Factor Authentication Verification Modal
 *
 * Displayed during login when 2FA is enabled for the user.
 * Supports TOTP verification and backup code fallback.
 */

import { useState, useRef, useEffect } from 'react';
import { ShieldCheck, Key, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TwoFactorVerifyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (token: string, isBackupCode: boolean) => Promise<void>;
  partialToken: string;
  error?: string;
}

export function TwoFactorVerifyModal({
  isOpen,
  onClose,
  onVerify,
  error,
}: TwoFactorVerifyModalProps) {
  const [mode, setMode] = useState<'totp' | 'backup'>('totp');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, mode]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCode('');
      setMode('totp');
      setLocalError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Validate input
    if (mode === 'totp') {
      if (!/^\d{6}$/.test(code)) {
        setLocalError('Please enter a 6-digit code');
        return;
      }
    } else {
      if (!/^[A-Za-z0-9]{8}$/.test(code)) {
        setLocalError('Backup code must be 8 characters');
        return;
      }
    }

    setIsLoading(true);
    try {
      await onVerify(code, mode === 'backup');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (mode === 'totp') {
      // Only allow digits for TOTP
      if (/^\d*$/.test(value) && value.length <= 6) {
        setCode(value);
      }
    } else {
      // Alphanumeric for backup codes
      if (/^[A-Za-z0-9]*$/.test(value) && value.length <= 8) {
        setCode(value.toUpperCase());
      }
    }
    setLocalError(null);
  };

  const switchMode = () => {
    setCode('');
    setLocalError(null);
    setMode(mode === 'totp' ? 'backup' : 'totp');
  };

  const displayError = error || localError;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-blue-100 rounded-full">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
            </div>
            <DialogTitle>Two-Factor Authentication</DialogTitle>
          </div>
          <DialogDescription>
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app'
              : 'Enter one of your backup codes'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="2fa-code">
              {mode === 'totp' ? 'Verification Code' : 'Backup Code'}
            </Label>
            <Input
              ref={inputRef}
              id="2fa-code"
              type="text"
              inputMode={mode === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              placeholder={mode === 'totp' ? '000000' : 'XXXXXXXX'}
              value={code}
              onChange={handleCodeChange}
              className={`text-center text-2xl tracking-widest font-mono ${
                displayError ? 'border-red-500' : ''
              }`}
              disabled={isLoading}
            />
          </div>

          {displayError && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
              <AlertTriangle className="h-4 w-4" />
              <span>{displayError}</span>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || (mode === 'totp' ? code.length !== 6 : code.length !== 8)}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={switchMode}
              className="text-sm"
              disabled={isLoading}
            >
              <Key className="mr-2 h-4 w-4" />
              {mode === 'totp' ? 'Use backup code instead' : 'Use authenticator app'}
            </Button>
          </div>
        </form>

        {mode === 'backup' && (
          <div className="mt-4 p-3 bg-amber-50 rounded-md">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> Each backup code can only be used once. After using this code,
              it will be invalidated.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
